// The player on the scooter, and the other scooter riders.
import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { PI } from '../../lib/math.js';
import { Ph } from '../../lib/factory.js';
import { nylonTex } from '../../textures/cloth.js';
import { V } from '../../world/sagrada.js';
import { sitSpots, standSpots } from '../crowd.js';
import { BIKE_SCALE, lean, riderG, visorMat } from '../../vehicles/scooter.js';
import { npcBikes } from '../../vehicles/traffic.js';
import { toonify } from '../../core/optimise.js';
import { state } from '../../core/state.js';
import { P_ } from './loader.js';
import { prepared } from './prepared.js';
import { crowd, outfit } from './outfits.js';
import { seat } from './seating.js';

export let helmetGeo;

export function buildRider() {
  // open-face jet helmet: a slightly long dome with a peaked visor, sized to the head bone
  helmetGeo = new THREE.SphereGeometry(1, 24, 16, 0, PI * 2, 0, PI * 0.62).scale(1, 0.95, 1.1);
  const visorGeo = new THREE.SphereGeometry(1.03, 24, 10, PI / 2 - 0.85, 1.7, 0.95, 0.7).scale(
    1,
    0.95,
    1.1,
  );
  // the player: short dark hair, dark riding jacket, jeans, white trainers
  const ME = {
    Purple: 0x2b3240,
    LightBlue: 0x2f3c55,
    White: 0xe8e6e0,
    Hair: 0x2b1d14,
    Eyebrows: 0x2b1d14,
  };
  function makeRider(P, helmetCol, holder, me) {
    // stylised short arms: riders sit a touch larger and further forward than walkers so their hands reach the grips
    const { model, b } = seat(
      P,
      holder,
      (1.22 * P.k) / BIKE_SCALE,
      V(0, 1.2, -0.25),
      'ride',
      (sg) => V(sg * 0.36, 1.42, 0.36),
      // the base model wears shorts: its bare shins become jeans
      me
        ? (name, mesh) =>
            mesh === 'Casual_Legs_1'
              ? new THREE.Color(ME.LightBlue)
              : name in ME
                ? new THREE.Color(ME[name])
                : null
        : outfit(),
    );
    if (me) {
      // laptop backpack on his back, tilted with the torso lean (.3 + .15), straps over the chest
      const th = 0.45,
        c = holder.worldToLocal(b('Chest').getWorldPosition(new THREE.Vector3()));
      const back = V(0, Math.sin(th), -Math.cos(th)),
        up = V(0, Math.cos(th), Math.sin(th)),
        at = (d, u, x = 0) => c.clone().addScaledVector(back, d).addScaledVector(up, u).setX(x);
      const pk = new THREE.Group();
      holder.add(pk);
      pk.userData.rider = true;
      const put = (geo, mat, p) => {
        const m = new THREE.Mesh(geo, mat);
        m.position.copy(p);
        m.rotation.x = th;
        m.castShadow = true;
        toonify(mat);
        pk.add(m);
        return m;
      };
      const nylon = P_(0xffffff, { map: nylonTex, roughness: 0.75, metalness: 0 }),
        trim = P_(0x1c1c1e, { roughness: 0.6 });
      put(new RoundedBoxGeometry(0.42, 0.54, 0.19, 3, 0.07), nylon, at(0.2, -0.17));
      put(
        new RoundedBoxGeometry(0.3, 0.2, 0.06, 2, 0.03),
        P_(0x33373d, { roughness: 0.7 }),
        at(0.31, -0.27),
      );
      put(new THREE.TorusGeometry(0.06, 0.015, 6, 16, PI), trim, at(0.2, 0.12));
      for (const sx of [-1, 1]) {
        put(new RoundedBoxGeometry(0.05, 0.44, 0.025, 1, 0.01), trim, at(-0.15, -0.02, sx * 0.12));
        put(new RoundedBoxGeometry(0.05, 0.03, 0.26, 1, 0.01), trim, at(0.05, 0.18, sx * 0.12));
      }
    }
    // big enough to swallow the hair mesh (ponytails, quiffs) of every model
    const head = b('Head'),
      R = 0.1 / head.getWorldScale(new THREE.Vector3()).x;
    const helmet = new THREE.Mesh(helmetGeo, Ph(helmetCol, { roughness: 0.2, metalness: 0.3 }));
    helmet.scale.setScalar(R);
    helmet.position.set(0, R * 0.62, -R * 0.06);
    head.add(helmet);
    const visor = new THREE.Mesh(visorGeo, visorMat);
    visor.scale.setScalar(R);
    visor.position.copy(helmet.position);
    head.add(visor);
    return model;
  }
  if (window.dbg)
    Object.assign(window.dbg, { prepared, sitSpots, standSpots, jetSki: state.jetSki });
  makeRider(prepared[0], 0x141416, lean, true);
  riderG.visible = false;
  npcBikes.forEach((bk, i) =>
    makeRider(
      crowd[(i * 3 + 2) % crowd.length],
      [0xf2f0ea, 0xd9412c, 0x1d1d1d, 0xf2c014, 0x2f6fd0][i],
      bk,
    ),
  );
}
